import React from 'react'
import styled, { css } from 'styled-components'

import { useTranslation } from '../../i18n'
import {
  calcLineHeightOffset,
  fixIe,
  getColor,
  getFontSize,
  objectFit
} from '../../utils/cssUtils'
import mq from '../../utils/mediaQuery'
import Picture from '../Picture'
import EllipsisText from '../Text/EllipsisText'

const Article = styled.div`
  position: relative;
  display: flex;
  width: 792px;
  height: 159px;
  margin: 0 auto 24px;
  overflow: hidden;
  border-radius: 4px;
  box-shadow: inset 0 0 0 1px rgba(181, 181, 181, 0.4);
  font-size: ${getFontSize('body2')};
  color: ${getColor('grey')};
  transition: box-shadow 0.3s linear;
  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    border-radius: 4px;
    border: 1px solid transparent;
    transition: border-color 0.3s linear;
  }
  ${({ isAppview }) =>
    !isAppview &&
    css`
      &:hover {
        box-shadow: 0 0 16px 0 rgba(227, 39, 60, 0.2);
        cursor: pointer;
      }
      &:hover:after {
        border: 1px solid ${getColor('pink')};
      }
    `}
  ${mq.tablet} {
    width: 640px;
    height: 155px;
  }
  ${mq.mobile} {
    width: 100%;
    height: 96px;
    margin-bottom: 16px;
  }
`
const ArticleImgWrapper = styled.div`
  flex-shrink: 0;
  width: 283px;
  height: 100%;
  margin: 0;
  overflow: hidden;
  position: relative;
  ${mq.tablet} {
    width: 276px;
  }
  ${mq.mobile} {
    width: 160px;
  }
  background-color: ${getColor('skeleton')};
`
const ArticleImg = styled(Picture)`
  > img {
    width: 100%;
    height: 100%;
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    object-fit: cover;
    object-position: 50% 50%;
    ${objectFit('cover', '50% 50%')}
  }
`

const ArticleInfo = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  flex-grow: 1;
  padding: 16px;
  ${mq.mobile} {
    padding: 12px;
  }
  // ie11 fix
  width: 100%;
`
const Title = styled(props => <EllipsisText as='h3' line={2} {...props} />)`
  flex-shrink: 0;
  font-size: ${getFontSize('title')};
  color: ${getColor('black')};
  line-height: 1.5;
  margin-top: ${calcLineHeightOffset(18, 1.5)}px;
  margin-bottom: ${calcLineHeightOffset(18, 1.5) + 8}px;
  ${mq.tablet} {
    margin-bottom: ${calcLineHeightOffset(18, 1.5) + 6}px;
  }
  ${mq.mobile} {
    font-size: ${getFontSize('body2')};
    margin-bottom: ${calcLineHeightOffset(16, 1.5) + 4}px;
  }
`
const Description = styled(props => (
  <EllipsisText as='p' line={2} {...props} />
))`
  flex-shrink: 0;
  font-size: ${getFontSize('body2')};
  line-height: 1.5;
  margin-top: ${calcLineHeightOffset(16, 1.5)}px;
  margin-bottom: ${calcLineHeightOffset(16, 1.5) + 8}px;
  ${mq.tablet} {
    margin-bottom: ${calcLineHeightOffset(16, 1.5) + 6}px;
  }
  ${mq.mobile} {
    display: none !important;
  }
  ${fixIe(css`
    display: flex;
    overflow: hidden;
    max-height: ${16 * 1.5 * 2}px;
  `)}
`
const Date = styled.div`
  flex-shrink: 0;
  font-size: ${getFontSize('body1')};
`
const Label = styled.span``
const Value = styled.span`
  display: inline-block;
  color: ${getColor('black')};
  margin-left: 4px;
`

const ArticleItem = React.memo(
  ({
    title,
    description,
    mainImage,
    publishTime,
    imagesSizes,
    isAppview,
    ...props
  }) => {
    const { t } = useTranslation('common')

    return (
      <Article isAppview={isAppview}>
        <ArticleImgWrapper>
          <ArticleImg image={mainImage} sizes={imagesSizes} />
        </ArticleImgWrapper>
        <ArticleInfo>
          <Title>{title}</Title>
          <Description data-test-id='articleItem-description'>
            {description}
          </Description>
          <Date>
            <Label>{t('article.articleDate')}</Label>
            <Value>{publishTime}</Value>
          </Date>
        </ArticleInfo>
      </Article>
    )
  }
)

export default ArticleItem
